import React, {
  FC, useCallback, useContext, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCheckedSelector, setChecked } from 'features/details/response';
import { setFocusedPosition } from 'features/details/map';
import { Responder, ResponseGroupType } from '@x-guard/xgac-types/xgac';
import { Image } from 'components/molecules';
import styles from './style.module.scss';

import { ContactChip } from '../contactChip';
import { SubsectionLayout } from '../subsectionLayout';
import { ContactSwitch } from '../contactSwitch';
import { alarmContext } from '../../alarmContext';
import { getResponderCanBeContacted } from '../../contact/contactOptions';
import { DistanceLabel } from './distanceLabel';
import { useSelectProcedurePriority } from '../../procedure/useProcedurePriority';

type ContactableSubsectionProps = {
  responder: Responder;
  type?: ResponseGroupType;
  hideDistanceLabel?: boolean;
  hideContactChip?: boolean;
  /** The person who made the alarm: asks whether they need help instead of whether they help. */
  alarmCreator?: boolean;
};

/**
 * Represents the section for a contactable (Like an asset or static / dynamic response)
 */
export const ContactableSubsection: FC<ContactableSubsectionProps> = ({
  responder,
  type,
  hideDistanceLabel,
  hideContactChip,
  alarmCreator,
}) => {

  const {
    onClickContact,
  } = useContext(alarmContext);

  const dispatch = useDispatch();
  const selectPriority = useSelectProcedurePriority();

  // Responders who are asked to come help; police / APR / the alarm creator keep the plain switch.
  const helpsOrNot = [
    ResponseGroupType.Nearby,
    ResponseGroupType.Zone,
    ResponseGroupType.Personal,
    'responsePartners',
  ].includes(type);

  const labels = (alarmCreator && 'alarmCreator') || (helpsOrNot && 'helper') || undefined;

  const checked = useSelector(getCheckedSelector(responder));

  const dispatchSetChecked = useCallback((value: boolean | 'unknown' = true) => {

    dispatch(setChecked({
      responder,
      checked: value,
    }));

    // The alarm creator needs help: switch the procedure to helping
    if (alarmCreator && value === false) selectPriority('help');

  }, [responder, dispatch, alarmCreator, selectPriority]);

  const canBeContacted = useMemo(() => {

    return getResponderCanBeContacted(responder);

  }, [responder]);

  const onClickContactSwitch = useCallback((contactType) => {

    onClickContact({
      responder,
      contactType,
      onContactConfirmation: dispatchSetChecked,
    });

  }, [dispatchSetChecked, onClickContact, responder]);

  const onClick = useCallback(() => {

    const trackable = [
      ResponseGroupType.Nearby,
      ResponseGroupType.AlarmProfessionalResponder,
      'responsePartners',
    ].includes(type) || !type;

    if (trackable && checked === true && responder.info.position) {

      dispatch(setFocusedPosition(responder.info.position.coordinates));

    }

  }, [type, responder, checked, dispatch]);

  return (
    <SubsectionLayout
      onClick={onClick}
      personId={responder.info._id}
      guide={{
        name: responder.info.name, undecided: checked === null, alarmCreator, organisation: type === ResponseGroupType.Police,
      }}
      left={(
        <>
          <ContactSwitch
            checked={checked}
            setChecked={dispatchSetChecked}
            labels={labels}
          />
          {responder.info.image && (
            <Image src={responder.info.image} alt="" className={styles.photo}/>
          )}
          <div className={styles.name}>
            {responder.info.name}
          </div>
        </>
      )}
      right={(
        <>
          {(!hideContactChip && canBeContacted) ? (
            <ContactChip
              contactType={type === ResponseGroupType.Police ? 'phoneCall' : null}
              onClick={onClickContactSwitch}
            />
          ) : null}
          {(!hideDistanceLabel && responder.info.position) ? (
            <DistanceLabel position={responder.info.position}/>
          ) : null}
        </>
      )}
    />
  );

};
